import React, { useState } from 'react';
import { useHighlightsData } from '../context/HighlightsContext';
import pb from '../lib/pocketbase';
import { FiPlus, FiEdit2, FiTrash2, FiImage, FiSave, FiX, FiCheckCircle, FiChevronUp, FiChevronDown } from 'react-icons/fi';

export default function AdminHighlightsManager() {
    const { highlights, loading } = useHighlightsData();
    const [editingId, setEditingId] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [formData, setFormData] = useState({
        label: '',
        title: '',
        subtitle: '',
        button_text: '',
        link: '',
        color: 'blue',
        order: 0,
        is_active: true
    });
    const [selectedFile, setSelectedFile] = useState(null);

    const colors = [
        { name: 'Bleu', value: 'blue' },
        { name: 'Vert', value: 'green' },
        { name: 'Violet', value: 'purple' },
        { name: 'Indigo', value: 'indigo' },
        { name: 'Orange', value: 'orange' }
    ];

    const handleEdit = (highlight) => {
        setEditingId(highlight.id);
        setFormData({
            label: highlight.label,
            title: highlight.title,
            subtitle: highlight.subtitle || '',
            button_text: highlight.button_text || '',
            link: highlight.link || '',
            color: highlight.color || 'blue',
            order: highlight.order || 0,
            is_active: highlight.is_active !== false
        });
        setSelectedFile(null);
    };

    const handleCancel = () => {
        setEditingId(null);
        setFormData({ label: '', title: '', subtitle: '', button_text: '', link: '', color: 'blue', order: 0, is_active: true });
        setSelectedFile(null);
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setIsSaving(true);
        try {
            const data = new FormData();
            data.append('label', formData.label);
            data.append('title', formData.title);
            data.append('subtitle', formData.subtitle);
            data.append('button_text', formData.button_text);
            data.append('link', formData.link);
            data.append('color', formData.color);
            data.append('order', formData.order);
            data.append('is_active', formData.is_active);
            if (selectedFile) {
                data.append('image', selectedFile);
            }

            if (editingId && editingId !== 'new') {
                await pb.collection('home_highlights').update(editingId, data);
            } else {
                await pb.collection('home_highlights').create(data);
            }
            handleCancel();
        } catch (error) {
            console.error('Error saving highlight:', error);
            alert('Erreur lors de la sauvegarde');
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Supprimer cette annonce ?')) {
            try {
                await pb.collection('home_highlights').delete(id);
            } catch (error) {
                console.error('Error deleting highlight:', error);
            }
        }
    };

    if (loading) return <div className="admin-loading">Chargement...</div>;

    return (
        <div className="admin-content-inner">
            <div className="admin-header-actions">
                <div>
                    <h2 className="admin-page-title">Annonces (Accueil)</h2>
                    <p className="admin-page-subtitle">Gérez les cartes du slider "Bienvenue" sur la page d'accueil.</p>
                </div>
                {!editingId && (
                    <button className="btn btn-primary" onClick={() => setEditingId('new')}>
                        <FiPlus /> Ajouter une annonce
                    </button>
                )}
            </div>

            {editingId && (
                <div className="edit-card-overlay" onClick={(e) => e.target.className === 'edit-card-overlay' && handleCancel()}>
                    <div className="admin-card edit-card animate-fade-in">
                        <div className="admin-card-header" style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h3 style={{ margin: 0 }}>{editingId === 'new' ? 'Nouvelle annonce' : 'Modifier l\'annonce'}</h3>
                            <button className="btn-icon" onClick={handleCancel}><FiX /></button>
                        </div>
                        <form onSubmit={handleSave}>
                            <div className="admin-form-grid">
                                <div className="admin-input-group">
                                    <label>Label (ex: ACTUALITÉ)</label>
                                    <input
                                        type="text"
                                        required
                                        value={formData.label}
                                        onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                                        placeholder="Libellé en haut de carte"
                                    />
                                </div>
                                <div className="admin-input-group">
                                    <label>Titre principal</label>
                                    <input
                                        type="text"
                                        required
                                        value={formData.title}
                                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                        placeholder="Ex: Nous cherchons un Tech Lead !"
                                    />
                                </div>
                                <div className="admin-input-group">
                                    <label>Sous-titre / Date</label>
                                    <input
                                        type="text"
                                        value={formData.subtitle}
                                        onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                                        placeholder="Ex: 22 Jan 2026 ou CDD - Paris"
                                    />
                                </div>
                                <div className="admin-input-group">
                                    <label>Texte du bouton</label>
                                    <input
                                        type="text"
                                        value={formData.button_text}
                                        onChange={(e) => setFormData({ ...formData, button_text: e.target.value })}
                                        placeholder="Ex: Lire l'article"
                                    />
                                </div>
                                <div className="admin-input-group">
                                    <label>Lien (URL)</label>
                                    <input
                                        type="text"
                                        value={formData.link}
                                        onChange={(e) => setFormData({ ...formData, link: e.target.value })}
                                        placeholder="Ex: /recrutement ou https://..."
                                    />
                                </div>
                                <div className="admin-input-group">
                                    <label>Couleur d'accentuation</label>
                                    <select
                                        value={formData.color}
                                        onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                                    >
                                        {colors.map(c => <option key={c.value} value={c.value}>{c.name}</option>)}
                                    </select>
                                </div>
                                <div className="admin-input-group">
                                    <label>Ordre</label>
                                    <input
                                        type="number"
                                        value={formData.order}
                                        onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                                    />
                                </div>
                                <div className="admin-input-group">
                                    <label>Image d'illustration</label>
                                    <div className="file-input-wrapper">
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={(e) => setSelectedFile(e.target.files[0])}
                                        />
                                        <FiImage className="file-icon" />
                                    </div>
                                </div>
                                <div className="admin-input-group checkbox-group" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <input
                                        type="checkbox"
                                        id="is_active"
                                        checked={formData.is_active}
                                        onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                                    />
                                    <label htmlFor="is_active" style={{ marginBottom: 0 }}>Annonce active (visible sur le site)</label>
                                </div>
                            </div>

                            <div className="admin-form-actions" style={{ marginTop: '2rem', display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
                                <button type="button" className="btn btn-ghost" onClick={handleCancel}>
                                    Annuler
                                </button>
                                <button type="submit" className="btn btn-primary" disabled={isSaving}>
                                    {isSaving ? 'Enregistrement...' : editingId === 'new' ? 'Créer' : 'Mettre à jour'} <FiSave />
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <div className="admin-highlights-list">
                <div className="admin-table-wrap">
                    <table className="admin-table">
                        <thead>
                            <tr>
                                <th>Ordre</th>
                                <th>Image</th>
                                <th>Infos</th>
                                <th>Lien / Bouton</th>
                                <th>Status</th>
                                <th className="text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {highlights.length === 0 ? (
                                <tr>
                                    <td colSpan="6" className="text-center py-4">Aucune annonce trouvée.</td>
                                </tr>
                            ) : (
                                highlights.map((h) => (
                                    <tr key={h.id} className="admin-table-row">
                                        <td width="60" className="text-center">
                                            <span className="order-badge">{h.order}</span>
                                        </td>
                                        <td width="100">
                                            <div className="admin-table-image">
                                                {h.image ? (
                                                    <img
                                                        src={pb.files.getUrl(h, h.image)}
                                                        alt=""
                                                    />
                                                ) : (
                                                    <div className="image-placeholder">
                                                        <FiImage />
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                        <td>
                                            <div className="admin-info-cell">
                                                <div className="info-label-row">
                                                    <span className={`marker-dot marker-${h.color}`}></span>
                                                    <span className="info-label">{h.label}</span>
                                                </div>
                                                <div className="info-title">{h.title}</div>
                                                {h.subtitle && <div className="info-subtitle">{h.subtitle}</div>}
                                            </div>
                                        </td>
                                        <td>
                                            <div className="admin-link-cell">
                                                <div className="link-button-text">{h.button_text || 'Découvrir'}</div>
                                                <div className="link-url">{h.link}</div>
                                            </div>
                                        </td>
                                        <td width="100">
                                            <span className={`admin-status-badge ${h.is_active ? 'active' : 'inactive'}`}>
                                                {h.is_active ? 'Actif' : 'Masqué'}
                                            </span>
                                        </td>
                                        <td width="100" className="text-right">
                                            <div className="admin-item-actions">
                                                <button className="btn-icon edit" onClick={() => handleEdit(h)} title="Modifier"><FiEdit2 /></button>
                                                <button className="btn-icon delete" onClick={() => handleDelete(h.id)} title="Supprimer"><FiTrash2 /></button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

        </div>
    );
}
